import { api } from '@/lib/api/client'
import type {
  ProjectWorkflows,
  Timeline,
  Workflow,
  WorkflowTemplate,
} from '@/types/workflow'

interface Collection<T> {
  data: T[]
}

export async function fetchWorkflows(): Promise<Workflow[]> {
  const { data } = await api.get<Collection<Workflow>>('/workflows')

  return data.data
}

export async function fetchWorkflowTemplates(): Promise<WorkflowTemplate[]> {
  const { data } = await api.get<Collection<WorkflowTemplate>>('/workflows/templates')

  return data.data
}

export async function createWorkflowFromTemplate(
  template: string,
  name?: string,
): Promise<Workflow> {
  const { data } = await api.post<{ data: Workflow }>('/workflows', { template, name })

  return data.data
}

export async function updateWipLimit(
  workflowId: string,
  stateId: string,
  wipLimit: number | null,
): Promise<Workflow> {
  const { data } = await api.patch<{ data: Workflow }>(
    `/workflows/${workflowId}/states/${stateId}`,
    { wipLimit },
  )

  return data.data
}

export async function fetchProjectWorkflows(projectKey: string): Promise<ProjectWorkflows> {
  const { data } = await api.get<{ data: ProjectWorkflows }>(`/projects/${projectKey}/workflows`)

  return data.data
}

/** Switching carries the cards across by category; the response says how many moved. */
export async function setProjectDeliveryWorkflow(
  projectKey: string,
  deliveryWorkflowId: string,
): Promise<ProjectWorkflows> {
  const { data } = await api.put<{ data: ProjectWorkflows }>(
    `/projects/${projectKey}/workflows`,
    { deliveryWorkflowId },
  )

  return data.data
}

export async function fetchTimeline(projectKey: string): Promise<Timeline> {
  const { data } = await api.get<{ data: Timeline }>(`/projects/${projectKey}/timeline`)

  return data.data
}

/* ── States ────────────────────────────────────────────────────────────── */

export async function fetchWorkflow(id: string): Promise<Workflow> {
  const { data } = await api.get<{ data: Workflow }>(`/workflows/${id}`)

  return data.data
}

export async function createWorkflowState(
  workflowId: string,
  input: { name: string; category: string; wipLimit?: number | null; color?: string | null },
): Promise<Workflow> {
  const { data } = await api.post<{ data: Workflow }>(`/workflows/${workflowId}/states`, input)

  return data.data
}

export async function deleteWorkflowState(
  workflowId: string,
  stateId: string,
  moveTo: string,
): Promise<Workflow> {
  // Cards cannot be left without a column, so the server requires somewhere to
  // put them and records the move in each item's activity.
  const { data } = await api.delete<{ data: Workflow }>(
    `/workflows/${workflowId}/states/${stateId}`,
    { data: { moveTo } },
  )

  return data.data
}

export async function reorderWorkflowStates(
  workflowId: string,
  stateIds: string[],
): Promise<Workflow> {
  const { data } = await api.post<{ data: Workflow }>(`/workflows/${workflowId}/states/reorder`, {
    stateIds,
  })

  return data.data
}

/* ── Transitions ───────────────────────────────────────────────────────── */

export interface WorkflowTransition {
  id: string
  fromStateId: string | null
  toStateId: string
}

export async function fetchTransitions(workflowId: string): Promise<WorkflowTransition[]> {
  const { data } = await api.get<{ data: WorkflowTransition[] }>(
    `/workflows/${workflowId}/transitions`,
  )

  return data.data
}

export async function createTransition(
  workflowId: string,
  input: { fromStateId: string | null; toStateId: string },
): Promise<WorkflowTransition> {
  const { data } = await api.post<{ data: WorkflowTransition }>(
    `/workflows/${workflowId}/transitions`,
    input,
  )

  return data.data
}

export async function deleteTransition(workflowId: string, transitionId: string): Promise<void> {
  await api.delete(`/workflows/${workflowId}/transitions/${transitionId}`)
}

/* ── Automation ────────────────────────────────────────────────────────── */

export type AutomationEvent =
  | 'work_item.created'
  | 'work_item.state_changed'
  | 'work_item.assigned'
  | 'work_item.closed'

/** `set_visibility` is deliberately not an action — see AutomationEngine. */
export type AutomationActionType = 'set_state' | 'set_priority' | 'set_assignee'

export interface AutomationRule {
  id: string
  name: string
  event: AutomationEvent
  conditions: { field: string; operator: string; value?: unknown }[]
  actions: { type: AutomationActionType; value?: unknown }[]
  isEnabled: boolean
  position: number
}

export async function fetchAutomationRules(workflowId: string): Promise<AutomationRule[]> {
  const { data } = await api.get<{ data: AutomationRule[] }>(`/workflows/${workflowId}/automations`)

  return data.data
}

export async function createAutomationRule(
  workflowId: string,
  input: {
    name: string
    event: AutomationEvent
    conditions?: { field: string; operator: string; value?: unknown }[]
    actions: { type: AutomationActionType; value?: unknown }[]
    isEnabled?: boolean
  },
): Promise<AutomationRule> {
  const { data } = await api.post<{ data: AutomationRule }>(
    `/workflows/${workflowId}/automations`,
    input,
  )

  return data.data
}

export async function updateAutomationRule(
  workflowId: string,
  ruleId: string,
  changes: { name?: string; isEnabled?: boolean; actions?: { type: AutomationActionType; value?: unknown }[] },
): Promise<AutomationRule> {
  const { data } = await api.patch<{ data: AutomationRule }>(
    `/workflows/${workflowId}/automations/${ruleId}`,
    changes,
  )

  return data.data
}

export async function deleteAutomationRule(workflowId: string, ruleId: string): Promise<void> {
  await api.delete(`/workflows/${workflowId}/automations/${ruleId}`)
}

/** What a rule actually did, so a surprising board change can be traced to it. */
export async function fetchRuleFirings(
  workflowId: string,
  ruleId: string,
): Promise<{ id: string; occurredAt: string; verb: string; changes: unknown }[]> {
  const { data } = await api.get<{ data: { id: string; occurredAt: string; verb: string; changes: unknown }[] }>(
    `/workflows/${workflowId}/automations/${ruleId}/firings`,
  )

  return data.data
}
